<template>
  <div class="page-container animate-fade-in-up">
    <PageHeader icon="el-icon-s-data" title="数据总览" subtitle="系统核心数据统计与趋势分析" />

    <!-- 统计卡片 -->
    <div class="stats-grid">
      <div class="stat-card">
        <div class="stat-icon" style="background: linear-gradient(135deg, #BFDBFE, #93C5FD);">
          <i class="el-icon-user" style="color: #1E40AF;"></i>
        </div>
        <div class="stat-info">
          <div class="stat-value">{{ employeeCount }}</div>
          <div class="stat-label">在职员工</div>
        </div>
      </div>
      <div class="stat-card">
        <div class="stat-icon" style="background: linear-gradient(135deg, #FEF3C7, #FDE68A);">
          <i class="el-icon-document" style="color: #92400E;"></i>
        </div>
        <div class="stat-info">
          <div class="stat-value">{{ evaluationCount }}</div>
          <div class="stat-label">评估记录</div>
        </div>
      </div>
      <div class="stat-card">
        <div class="stat-icon" style="background: linear-gradient(135deg, #FECACA, #FCA5A5);">
          <i class="el-icon-warning" style="color: #991B1B;"></i>
        </div>
        <div class="stat-info">
          <div class="stat-value">{{ highRiskCount }}</div>
          <div class="stat-label">高风险人数</div>
        </div>
      </div>
      <div class="stat-card">
        <div class="stat-icon" style="background: linear-gradient(135deg, #D1FAE5, #A7F3D0);">
          <i class="el-icon-user-solid" style="color: #065F46;"></i>
        </div>
        <div class="stat-info">
          <div class="stat-value">{{ resignedCount }}</div>
          <div class="stat-label">已离职人数</div>
        </div>
      </div>
    </div>

    <!-- 图表区域 -->
    <div class="charts-grid">
      <div class="chart-card">
        <div class="chart-header">
          <h3 class="chart-title">近 30 天评估趋势</h3>
        </div>
        <div ref="trendChart" class="chart-body" style="height: 280px;"></div>
      </div>
      <div class="chart-card">
        <div class="chart-header">
          <h3 class="chart-title">部门风险分布</h3>
        </div>
        <div ref="deptChart" class="chart-body" style="height: 280px;"></div>
      </div>
    </div>

    <!-- 排行 -->
    <div class="rank-section">
      <div class="rank-card">
        <div class="rank-header">
          <h3 class="rank-title">行为触发频率排行</h3>
        </div>
        <div class="rank-body">
          <div v-for="(item, i) in behaviorRank" :key="i" class="rank-item">
            <span class="rank-num" :class="i < 3 ? 'rank-top' : ''">{{ i + 1 }}</span>
            <span class="rank-name">{{ item.name }}</span>
            <div class="rank-bar-wrap">
              <div class="rank-bar" :style="{ width: item.percent + '%' }"></div>
            </div>
            <span class="rank-count">{{ item.count }}次</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import PageHeader from '../../components/PageHeader.vue'

export default {
  name: 'AdminDashboard',
  components: { PageHeader },
  data() {
    return {
      trendChart: null,
      deptChart: null
    }
  },
  computed: {
    currentTaskId() {
      return this.$store.getters.currentTaskId
    },
    employees() {
      // 只统计在职员工，防止后端返回的数据中包含已离职人员
      const list = this.$store.getters.taskEmployees || []
      return list.filter(e => e.employment_status === 1)
    },
    evaluations() {
      const all = this.$store.state.evaluations || []
      if (!this.currentTaskId) return all
      return all.filter(e => e.task_id === this.currentTaskId || !e.task_id)
    },
    resignedEmployees() {
      return this.$store.getters.taskResigned || []
    },
    employeeCount() { return this.employees.length },
    evaluationCount() { return this.evaluations.length },
    highRiskCount() {
      return this.employees.filter(e => e.latest_risk_level === 3).length
    },
    resignedCount() { return this.resignedEmployees.length },
    behaviorRank() {
      const map = {}
      this.evaluations.forEach(ev => {
        if (ev.answers) {
          ev.answers.forEach(a => {
            if (a && a.value === 1) {
              map[a.name] = (map[a.name] || 0) + 1
            }
          })
        }
      })
      const list = Object.keys(map).map(k => ({ name: k, count: map[k] }))
      list.sort((a, b) => b.count - a.count)
      const max = list.length ? list[0].count : 1
      return list.slice(0, 8).map(item => ({ ...item, percent: Math.round((item.count / max) * 100) }))
    }
  },
  mounted() {
    this.initTrendChart()
    this.initDeptChart()
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
    if (this.trendChart) this.trendChart.dispose()
    if (this.deptChart) this.deptChart.dispose()
  },
  methods: {
    handleResize() {
      if (this.trendChart) this.trendChart.resize()
      if (this.deptChart) this.deptChart.resize()
    },
    initTrendChart() {
      const echarts = require('echarts')
      const el = this.$refs.trendChart
      if (!el) return
      this.trendChart = echarts.init(el)
      const days = []
      const counts = []
      const now = new Date()
      for (let i = 29; i >= 0; i--) {
        const d = new Date(now)
        d.setDate(d.getDate() - i)
        const dateStr = d.toISOString().slice(0, 10)
        days.push(dateStr.slice(5))
        counts.push(this.evaluations.filter(e => e.created_at && e.created_at.startsWith(dateStr)).length)
      }
      this.trendChart.setOption({
        tooltip: { trigger: 'axis' },
        grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
        xAxis: { type: 'category', boundaryGap: false, data: days, axisLine: { lineStyle: { color: '#E5E7EB' } }, axisLabel: { color: '#6B7280' } },
        yAxis: { type: 'value', axisLine: { show: false }, splitLine: { lineStyle: { color: '#F3F4F6' } }, axisLabel: { color: '#6B7280' } },
        series: [{
          name: '评估次数',
          type: 'line',
          smooth: true,
          symbol: 'circle',
          symbolSize: 6,
          itemStyle: { color: '#F2C94C' },
          areaStyle: { color: { type: 'linear', x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: 'rgba(242,201,76,0.2)' }, { offset: 1, color: 'rgba(242,201,76,0.02)' }] } },
          data: counts
        }]
      })
    },
    initDeptChart() {
      const echarts = require('echarts')
      const el = this.$refs.deptChart
      if (!el) return
      this.deptChart = echarts.init(el)
      const deptMap = {}
      this.employees.forEach(e => {
        const d = e.dept_name || '未知'
        if (!deptMap[d]) deptMap[d] = { low: 0, medium: 0, high: 0 }
        const level = e.latest_risk_level
        if (!level || e.latest_score <= 0) return
        if (level === 3) deptMap[d].high++
        else if (level === 2) deptMap[d].medium++
        else deptMap[d].low++
      })
      const depts = Object.keys(deptMap)
      this.deptChart.setOption({
        tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
        legend: { data: ['低风险', '中风险', '高风险'], bottom: 0, textStyle: { color: '#6B7280' } },
        grid: { left: '3%', right: '4%', bottom: '10%', top: '5%', containLabel: true },
        xAxis: { type: 'category', data: depts, axisLine: { lineStyle: { color: '#E5E7EB' } }, axisLabel: { color: '#6B7280' } },
        yAxis: { type: 'value', axisLine: { show: false }, splitLine: { lineStyle: { color: '#F3F4F6' } }, axisLabel: { color: '#6B7280' } },
        series: [
          { name: '低风险', type: 'bar', stack: 'total', itemStyle: { color: '#34D399' }, data: depts.map(d => deptMap[d].low) },
          { name: '中风险', type: 'bar', stack: 'total', itemStyle: { color: '#F2C94C' }, data: depts.map(d => deptMap[d].medium) },
          { name: '高风险', type: 'bar', stack: 'total', itemStyle: { color: '#EF4444' }, data: depts.map(d => deptMap[d].high) }
        ]
      })
    }
  }
}
</script>

<style scoped>
.page-container { max-width: 1200px; margin: 0 auto; }
.stats-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: var(--space-xl); margin-bottom: var(--space-2xl); }
.stat-card { background: var(--color-surface); border: 1px solid var(--color-border); border-radius: var(--radius-lg); padding: var(--space-xl); display: flex; align-items: center; gap: var(--space-lg); box-shadow: var(--shadow-sm); transition: all var(--duration-normal) var(--ease-out); }
.stat-card:hover { box-shadow: var(--shadow-md); transform: translateY(-2px); }
.stat-icon { width: 48px; height: 48px; border-radius: var(--radius-md); display: flex; align-items: center; justify-content: center; font-size: 20px; }
.stat-value { font-size: 28px; font-weight: 700; color: var(--color-text); line-height: 1; margin-bottom: var(--space-xs); }
.stat-label { font-size: 13px; color: var(--color-text-secondary); }

.charts-grid { display: grid; grid-template-columns: 1fr 1fr; gap: var(--space-xl); margin-bottom: var(--space-2xl); }
.chart-card { background: var(--color-surface); border: 1px solid var(--color-border); border-radius: var(--radius-lg); padding: var(--space-xl); box-shadow: var(--shadow-sm); }
.chart-header { margin-bottom: var(--space-lg); }
.chart-title { font-size: 16px; font-weight: 600; color: var(--color-text); margin: 0; }

.rank-section { margin-bottom: var(--space-2xl); }
.rank-card { background: var(--color-surface); border: 1px solid var(--color-border); border-radius: var(--radius-lg); padding: var(--space-xl); box-shadow: var(--shadow-sm); }
.rank-header { margin-bottom: var(--space-lg); }
.rank-title { font-size: 16px; font-weight: 600; color: var(--color-text); margin: 0; }
.rank-body { display: flex; flex-direction: column; gap: var(--space-md); }
.rank-item { display: flex; align-items: center; gap: var(--space-md); }
.rank-num { width: 24px; height: 24px; border-radius: var(--radius-full); background: var(--color-bg); color: var(--color-text-muted); display: flex; align-items: center; justify-content: center; font-size: 12px; font-weight: 700; flex-shrink: 0; }
.rank-top { background: var(--color-primary); color: white; }
.rank-name { width: 120px; font-size: 13px; color: var(--color-text); flex-shrink: 0; }
.rank-bar-wrap { flex: 1; height: 8px; background: var(--color-bg); border-radius: var(--radius-full); overflow: hidden; }
.rank-bar { height: 100%; background: linear-gradient(90deg, var(--color-primary), var(--color-primary-light)); border-radius: var(--radius-full); transition: width var(--duration-slow) var(--ease-out); }
.rank-count { width: 50px; text-align: right; font-size: 13px; color: var(--color-text-secondary); flex-shrink: 0; }

@media (max-width: 900px) {
  .stats-grid { grid-template-columns: repeat(2, 1fr); }
  .charts-grid { grid-template-columns: 1fr; }
}
</style>
